import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Target, Brain, TrendingUp, MapPin, DollarSign,
    Building2, GraduationCap, Users, // Verifique se Users está aqui
    BookOpen, ChevronRight, Sun, Moon, ArrowRight,
    Layers, Activity, Eye, Zap, CheckCircle2
} from 'lucide-react';

// Importação dos componentes solicitados
import DotGrid from '../../components/home/DotGrid';
import ScrollStack, { ScrollStackItem } from '../../components/home/ScrollStack';

export default function HP() {
    const [dark, setDark] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const theme = {
        bg: dark ? 'bg-[#050505]' : 'bg-[#FDFDFD]',
        text: dark ? 'text-white' : 'text-slate-900',
        textMuted: dark ? 'text-slate-400' : 'text-slate-500',
        card: dark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-white border-slate-200 shadow-sm',
        accent: '#0974BA'
    };

    const colors = {
        blue: '#0974BA',
        green: '#13983D',
        yellow: '#F9B047',
        red: '#E83A3C',
    };

    return (
        <div className={`min-h-screen ${theme.bg} ${theme.text} transition-colors duration-500 selection:bg-blue-500/30 overflow-x-hidden`}>

            {/* BACKGROUND INTERATIVO (DotGrid) */}
            <div className="fixed inset-0 z-0 opacity-40 pointer-events-none md:pointer-events-auto">
                <DotGrid
                    dotSize={5}
                    gap={30}
                    proximity={120}
                    shockRadius={200}
                />
                <div className={`absolute inset-0 ${dark ? 'bg-gradient-to-b from-[#050505]/20 via-transparent to-[#050505]' : 'bg-gradient-to-b from-white/20 via-transparent to-white'}`} />
            </div>

            {/* NAVEGAÇÃO */}
            <nav className={`fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b ${dark ? 'border-white/5 bg-black/20' : 'border-slate-200 bg-white/40'}`}>
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => navigate('/')}>
                        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center group-hover:rotate-12 transition-transform">
                            <Zap size={18} className="text-white fill-current" />
                        </div>
                        <span className="font-black text-xl tracking-tighter">SIDEB<span className="text-blue-500">+</span></span>
                    </div>

                    <div className="hidden md:flex items-center gap-10 text-[11px] font-bold uppercase tracking-[0.2em]">
                        <a href="#recursos" className={`${theme.textMuted} hover:text-blue-500 transition-colors`}>Recursos</a>
                        <a href="#resultados" className={`${theme.textMuted} hover:text-blue-500 transition-colors`}>Resultados</a>
                        <a href="#publico" className={`${theme.textMuted} hover:text-blue-500 transition-colors`}>Público</a>
                    </div>

                    <div className="flex items-center gap-4">
                        <button onClick={() => setDark(!dark)} className="p-2.5 rounded-xl border border-transparent hover:border-blue-500/50 transition-all">
                            {dark ? <Sun size={20} className="text-yellow-400" /> : <Moon size={20} className="text-slate-600" />}
                        </button>
                        <button onClick={() => navigate('/login')} className="px-6 py-2.5 rounded-full text-sm font-bold bg-white text-black hover:bg-blue-500 hover:text-white transition-all shadow-lg">
                            Entrar
                        </button>
                    </div>
                </div>
            </nav>

            {/* HERO SECTION */}
            <section className="relative pt-40 pb-20 z-10">
                <div className="max-w-6xl mx-auto px-6 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-xs font-bold mb-8">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                        </span>
                        PLATAFORMA DE ALTA PERFORMANCE
                    </div>

                    <h1 className="text-5xl md:text-8xl font-black tracking-tighter mb-8 leading-[0.9]">
                        Calibre o <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400">
                            Futuro da Educação.
                        </span>
                    </h1>

                    <p className={`text-lg md:text-xl max-w-2xl mx-auto mb-12 font-medium leading-relaxed ${theme.textMuted}`}>
                        A ferramenta definitiva para análise de descritores, gestão de metas e diagnóstico de aprendizagem em tempo real.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button className="px-10 py-5 rounded-2xl bg-blue-600 text-white font-bold text-lg hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20">
                            Solicitar Demonstração
                        </button>
                        <button className={`px-10 py-5 rounded-2xl font-bold text-lg border ${theme.card} backdrop-blur-md`}>
                            Ver Resultados
                        </button>
                    </div>
                </div>
            </section>

            {/* SEÇÃO SCROLLSTACK (Recursos de Destaque) */}
            <section id="recursos" className="relative z-10 py-24">
                <div className="max-w-4xl mx-auto px-6 mb-16 text-center">
                    <h2 className="text-4xl font-black tracking-tighter mb-4">Por que escolher o SIDEB+?</h2>
                    <p className={theme.textMuted}>Role para descobrir como transformamos a gestão pedagógica.</p>
                </div>

                <div className="max-w-5xl mx-auto my-8 px-6">
                    <ScrollStack
                        stackPosition="5%"     // Ajuste isso para definir quão alto no topo elas param
                        itemStackDistance={25}  // Ajuste isso para definir quanto da carta de baixo aparece
                        itemScale={0.05}        // Diferença de tamanho entre as cartas empilhadas
                    >
                        <ScrollStackItem itemClassName={dark ? 'bg-zinc-900' : 'bg-blue-50'}>
                            <div className="flex flex-col md:flex-row h-full items-center gap-8">
                                <div className="flex-1">
                                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-6 text-white"><Target /></div>
                                    <h3 className="text-3xl font-black mb-4">Matriz de Descritores</h3>
                                    <p className={theme.textMuted}>Visualize exatamente quais habilidades sua rede já domina e onde estão os pontos cegos do aprendizado.</p>
                                </div>
                                <div className="hidden md:block w-64 h-40 bg-black/20 rounded-2xl border border-white/5 shadow-inner" />
                            </div>
                        </ScrollStackItem>

                        <ScrollStackItem itemClassName={dark ? 'bg-zinc-900' : 'bg-emerald-50'}>
                            <div className="flex flex-col md:flex-row h-full items-center gap-8">
                                <div className="flex-1">
                                    <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-6 text-white"><Activity /></div>
                                    <h3 className="text-3xl font-black mb-4">Monitoramento Real-time</h3>
                                    <p className={theme.textMuted}>Acompanhe a aplicação de provas e o engajamento das turmas no momento em que tudo acontece.</p>
                                </div>
                                <div className="hidden md:block w-64 h-40 bg-black/20 rounded-2xl border border-white/5 shadow-inner" />
                            </div>
                        </ScrollStackItem>

                        <ScrollStackItem itemClassName={dark ? 'bg-zinc-900' : 'bg-orange-50'}>
                            <div className="flex flex-col md:flex-row h-full items-center gap-8">
                                <div className="flex-1">
                                    <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-6 text-white"><Brain /></div>
                                    <h3 className="text-3xl font-black mb-4">Análise de Distratores</h3>
                                    <p className={theme.textMuted}>Vá além do certo ou errado. Entenda o raciocínio dos alunos através da análise estatística das alternativas marcadas.</p>
                                </div>
                                <div className="hidden md:block w-64 h-40 bg-black/20 rounded-2xl border border-white/5 shadow-inner" />
                            </div>
                        </ScrollStackItem>
                    </ScrollStack>
                </div>
            </section>

            {/* STATS RÁPIDOS */}
            <section className="relative z-10 py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 p-8 rounded-[3rem] border ${theme.card} backdrop-blur-xl`}>
                        {[
                            { label: 'Escolas', val: '500+', icon: <Layers size={16} /> },
                            { label: 'Alunos', val: '120k', icon: <Users size={16} /> },
                            { label: 'Precisão', val: '99.9%', icon: <CheckCircle2 size={16} /> },
                            { label: 'Uptime', val: '24/7', icon: <Activity size={16} /> },
                        ].map((s, i) => (
                            <div key={i} className="text-center group">
                                <div className="text-blue-500 mb-2 flex justify-center group-hover:scale-110 transition-transform">{s.icon}</div>
                                <div className="text-4xl font-black tracking-tighter">{s.val}</div>
                                <div className={`text-[10px] uppercase tracking-widest font-bold ${theme.textMuted}`}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="relative z-10 py-12 border-t border-white/5 mt-20">
                <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-2">
                        <span className="font-black tracking-tighter">SIDEB<span className="text-blue-500">+</span></span>
                        <span className={`text-[10px] font-bold ${theme.textMuted}`}>© 2026</span>
                    </div>
                    <div className="flex gap-8 text-[11px] font-bold uppercase tracking-widest opacity-60">
                        <a href="#" className="hover:text-blue-500">Privacidade</a>
                        <a href="#" className="hover:text-blue-500">Suporte</a>
                    </div>
                </div>
            </footer>
        </div>
    );
}